import { IDictionary } from "@Components/shared/types";
import { z } from "zod";

/**
 * Creates an empty object based on a Zod schema, populating it with default values.
 *
 * @param {T} schema - The Zod schema object defining the structure of the result
 *
 * @description
 * This function takes a Zod schema object and creates a new object with the same structure,
 * but populated with default values based on the types defined in the schema. It supports
 * nested objects and various Zod types including strings, numbers, booleans, arrays, and records.
 *
 */
export const createEmptyObjectFromSchema = <Type extends z.ZodObject<z.ZodRawShape>>(
    schema: Type,
    overrides: IDictionary = {}
): z.infer<Type> => {
    const result: IDictionary = {};

    for (const [key, value] of Object.entries(schema.shape)) {
        if (key in overrides) {
            result[key] = overrides[key];
            continue;
        }
        result[key] = getDefaultValue(value);
    }

    return result;
};

/**
 * Determines the default value for a given Zod schema type.
 *
 * @description
 * This function takes a Zod schema type and returns an appropriate default value:
 *
 * @example
 * getDefaultValue(z.string()) // Returns ""
 * getDefaultValue(z.number()) // Returns 0
 * getDefaultValue(z.boolean()) // Returns false
 * getDefaultValue(z.array(z.string())) // Returns []
 * getDefaultValue(z.record(z.string())) // Returns {}
 * getDefaultValue(z.object({ key: z.string() })) // Returns { key: "" }
 *
 * @throws {Error} If an unsupported Zod type is encountered
 */
const getDefaultValue = (schema: z.ZodTypeAny) => {
    if (schema instanceof z.ZodString) return "";
    if (schema instanceof z.ZodNumber) return 0;
    if (schema instanceof z.ZodBoolean) return false;
    if (schema instanceof z.ZodArray) return [];
    if (schema instanceof z.ZodRecord) return {};
    if (schema instanceof z.ZodObject) {
        return createEmptyObjectFromSchema(schema) as z.ZodTypeAny;
    }

    // This case should theoretically never be reached due to the SchemaType constraint,
    // but it's included for completeness and to handle any potential future extensions.
    return undefined;
};
